import produce from 'immer';
import { set } from 'lodash/fp';
import { Fragment, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { FormSection } from 'redux-form';
import styled from 'styled-components';

import { getCombinedEditCardConfigs } from 'dashboard/apps/InquiryTemplateEditor/CombinedStepPanel/utils';
import DeviceHandoffEditSection from 'dashboard/apps/InquiryTemplateEditor/DeviceHandoffEditSection';
import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import SectionAccordionCondensed from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordionCondensed';
import {
  TranslationEditSectionContainer,
  TranslationsConfig,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/TranslationsEditor';
import { EDITOR_COMBINED_STEPS_SCHEMA } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import {
  PanelHeaderSection,
  PanelHeaderTitle,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/PanelHeaderTitle';
import { ScreenHeaderSection } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenSectionsPanel/ScreenSectionsEditor';
import { getLabelStringForComponentName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/utils';
import { SCREEN_EDITOR_CONTROL_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import { getHumanReadableStepName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import TranslationEditSection from 'dashboard/apps/InquiryTemplateEditor/TranslationEditSection/TranslationEditSection';
import TranslationsPanel from 'dashboard/apps/InquiryTemplateEditor/TranslationsPanel';
import {
  CombinedStepConfig,
  InquiryTemplateVersionCombinedStep,
  InquiryTemplateVersionStep,
} from 'dashboard/models/inquiry-template-version';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import { CombinedStepClientData } from 'lib/interfaces/StepsConfig';

const Panel = styled.div`
  overflow: hidden;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
`;

const PanelContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ScrollContainer = styled.div`
  overflow: auto;
  flex: 1 0 0;
  min-height: 0;
  z-index: 0;
`;

interface CombinedStepPanelProps {
  basePath: string;
  title: string;
  step: InquiryTemplateVersionCombinedStep;
  stepPreview: CombinedStepClientData;
  availableBackSteps: InquiryTemplateVersionStep[];
  pageKeys: Array<keyof CombinedStepConfig['localizations']>;
  currentLocale: InquiryIso6391LanguageCode;
  enabledLocales: InquiryIso6391LanguageCode[];
  fieldSchema: FieldSchema[];
  editableUngated: boolean;
  getStepLocalizedStrings: (locales?: Array<InquiryIso6391LanguageCode>) => Promise<void>;
  updateSteps: ({ updatedStep }: { updatedStep: InquiryTemplateVersionStep }) => void;
}

const CombinedStepPanel = ({
  basePath,
  title,
  step,
  stepPreview,
  availableBackSteps,
  pageKeys,
  enabledLocales,
  currentLocale = 'en-US',
  fieldSchema,
  editableUngated,
  getStepLocalizedStrings,
  updateSteps,
}: CombinedStepPanelProps) => {
  const combinedStepSchema = EDITOR_COMBINED_STEPS_SCHEMA[step.type];
  const [translationsConfig, setTranslationsConfig] = useState<TranslationsConfig | null>(null);
  const editableForFree = useIsEditableWithOptions({
    accessTier: EditorAccessTier.InquiriesFree,
  });

  const handleTranslationUpdates = useCallback(
    (translationUpdates: Record<string, any>) => {
      const updatedStep = produce(step, (draftStep: InquiryTemplateVersionCombinedStep) => {
        Object.keys(translationUpdates).forEach((attributeKey) => {
          draftStep.config = set(attributeKey, translationUpdates[attributeKey], draftStep.config);
        });
      });
      updateSteps({
        updatedStep,
      });
    },
    [step, updateSteps],
  );

  const translationCards: ReactNode = useMemo(
    () =>
      getCombinedEditCardConfigs({
        step,
        stepPreview,
        pageKeys,
        basePath,
        combinedStepSchema,
      }).map((cardConfig) => (
        <TranslationEditSectionContainer key={`${cardConfig.name}-section`}>
          <TranslationEditSection
            config={cardConfig.section.config}
            label={
              combinedStepSchema
                ? cardConfig.section.label
                : getLabelStringForComponentName(cardConfig.name, step.type)
            }
            enabledLocales={enabledLocales}
            currentLocale={currentLocale}
            editable={editableUngated}
            getStepLocalizedStrings={getStepLocalizedStrings}
            handleOpenPanel={setTranslationsConfig}
            handleTranslationUpdates={handleTranslationUpdates}
          />
        </TranslationEditSectionContainer>
      )),
    [
      basePath,
      currentLocale,
      combinedStepSchema,
      editableUngated,
      enabledLocales,
      pageKeys,
      step,
      stepPreview,
      getStepLocalizedStrings,
      handleTranslationUpdates,
    ],
  );

  return (
    <Panel>
      <PanelHeaderSection>
        <PanelHeaderTitle
          basePath={basePath}
          title={getHumanReadableStepName(title)}
          subtitle='Screen'
          editable={editableUngated}
        />
      </PanelHeaderSection>
      <PanelContentContainer>
        <ScrollContainer>
          <ScreenHeaderSection
            step={step}
            availableBackSteps={availableBackSteps}
            editable={editableForFree}
            updateSteps={updateSteps}
          />
          <FormSection name='steps' component={Fragment}>
            <SectionAccordionCondensed title='Device Handoff'>
              <DeviceHandoffEditSection
                step={step}
                fieldSchema={fieldSchema}
                updateSteps={updateSteps}
              />
            </SectionAccordionCondensed>
            <SectionAccordionCondensed title='Translations'>
              {translationCards}
            </SectionAccordionCondensed>
            {translationsConfig != null && (
              <TranslationsPanel
                offsetElementId={SCREEN_EDITOR_CONTROL_PANEL_ID}
                config={translationsConfig}
                currentLocale={currentLocale}
                enabledLocales={enabledLocales}
                editable={editableUngated}
                getStepLocalizedStrings={getStepLocalizedStrings}
                onOutsideClick={() => setTranslationsConfig(null)}
                handleTranslationUpdates={handleTranslationUpdates}
              />
            )}
          </FormSection>
        </ScrollContainer>
      </PanelContentContainer>
    </Panel>
  );
};

export default memo(CombinedStepPanel);
